/**
 * The past tickets behind an SOP, listed beside the selected one's transcript. Read-only.
 * The mapped pool is what the SOP pipeline mapped, so it is empty for an SOP never
 * generated with ticket mapping on; the subcategory pool is every ticket filed there.
 */

import { useState, type UIEvent } from 'react';
import { Inbox, Loader2, MessagesSquare, Ticket } from 'lucide-react';
import { Sheet, SheetContent, SheetDescription, SheetFooter, SheetHeader, SheetTitle } from '@/components/ui/sheet';
import { Button } from '@/components/ui/button';
import { useSOPTickets, useTicketThread } from '@/hooks';
import { ListRow, MsgBubble } from '@/modules/simulator/conversations/conversation-parts';
import { threadToTranscript } from '@/services/ticket.service';
import type { SopTicketListItem } from '@/services/sop.service';
import type { SOP } from '@/types';
import { sopTitle } from '../format';

const customerLabel = (t: SopTicketListItem): string => t.customer?.name || t.customer?.email || 'Unknown customer';

export type TicketSheetSource = 'mapped' | 'subcategory';

interface CandidateTicketsBodyProps {
	sop: SOP;
	source: TicketSheetSource;
}

const CandidateTicketsBody = ({ sop, source }: CandidateTicketsBodyProps) => {
	const [selected, setSelected] = useState<SopTicketListItem | null>(null);
	const { tickets, isLoading, isError, isFetchNextPageError, hasMore, fetchNextPage } = useSOPTickets(sop.id, source);
	const { data: thread, isLoading: threadLoading, isError: threadError } = useTicketThread(selected?.ticketId ?? null);
	const transcript = thread ? threadToTranscript(thread) : [];

	const onListScroll = (e: UIEvent<HTMLDivElement>) => {
		const el = e.currentTarget;
		if (el.scrollTop + el.clientHeight >= el.scrollHeight - 120) fetchNextPage();
	};

	return (
		<>
			<SheetHeader className='border-b px-4 py-3'>
				<SheetTitle className='flex items-center gap-2 text-base capitalize'>
					<Ticket className='h-4 w-4 text-indigo-500' /> {sopTitle(sop.intent)}
				</SheetTitle>
				<SheetDescription>
					{source === 'subcategory'
						? `Every past ticket filed under ${sop.category ?? ''} / ${sop.subcategory ?? ''}: the pool the search continues into once the mapped tickets are settled.`
						: 'The past tickets this protocol was drafted from, as the ticket pipeline mapped them.'}
				</SheetDescription>
			</SheetHeader>

			<div className='flex min-h-0 flex-1'>
				<div className='flex w-[30%] min-w-[240px] shrink-0 flex-col border-r'>
					<div onScroll={onListScroll} className='min-h-0 flex-1 overflow-y-auto'>
						{isLoading ? (
							<div className='flex items-center justify-center gap-2 py-8 text-xs text-muted-foreground'>
								<Loader2 className='h-4 w-4 animate-spin' />
								Loading tickets
							</div>
						) : isError && tickets.length === 0 ? (
							<p className='px-4 py-10 text-center text-xs text-muted-foreground'>The tickets could not be loaded.</p>
						) : tickets.length === 0 ? (
							<div className='flex flex-col items-center gap-2 px-4 py-10 text-center text-xs text-muted-foreground'>
								<Inbox className='h-5 w-5' />
								{source === 'subcategory'
									? 'No past tickets exist under this subcategory.'
									: 'No tickets are mapped to this protocol yet. Tickets are mapped when it is drafted from its subcategory.'}
							</div>
						) : (
							<>
								{tickets.map((t) => (
									<ListRow
										key={t.ticketId}
										title={customerLabel(t)}
										when={t.resolvedAt}
										subject={t.subject}
										preview={t.summary}
										id={t.ticketId}
										channel={t.channel?.replace(/_/g, ' ')}
										selected={selected?.ticketId === t.ticketId}
										onSelect={() => setSelected(t)}
									/>
								))}
								{isFetchNextPageError ? (
									<div className='flex items-center justify-center gap-2 py-3 text-xs text-muted-foreground'>
										Could not load more tickets.
										<Button variant='ghost' size='sm' className='h-6 px-2 text-xs' onClick={fetchNextPage}>
											Retry
										</Button>
									</div>
								) : (
									hasMore && (
										<div className='flex justify-center py-3'>
											<Loader2 className='h-4 w-4 animate-spin text-muted-foreground' />
										</div>
									)
								)}
							</>
						)}
					</div>
				</div>

				<div className='flex min-h-0 flex-1 flex-col'>
					{!selected ? (
						<div className='flex flex-1 flex-col items-center justify-center gap-2 text-center text-sm text-muted-foreground'>
							<MessagesSquare className='h-6 w-6' />
							Select a ticket to read the conversation.
						</div>
					) : threadLoading ? (
						<div className='flex flex-1 items-center justify-center gap-2 text-xs text-muted-foreground'>
							<Loader2 className='h-4 w-4 animate-spin' />
							Loading messages
						</div>
					) : threadError || !thread ? (
						<p className='flex flex-1 items-center justify-center text-xs text-muted-foreground'>
							The conversation could not be loaded.
						</p>
					) : (
						<div className='flex flex-1 flex-col gap-3 overflow-y-auto p-4'>
							{transcript.map((m, i) => (
								<MsgBubble key={`${selected.ticketId}-${i}`} m={m} />
							))}
						</div>
					)}
				</div>
			</div>

			<SheetFooter className='flex-row items-center justify-between border-t px-4 py-3'>
				<span className='text-xs text-muted-foreground'>
					{selected
						? `#${selected.ticketId} · ${customerLabel(selected)} · ${transcript.length} message${transcript.length === 1 ? '' : 's'}`
						: 'No ticket selected'}
				</span>
			</SheetFooter>
		</>
	);
};

interface CandidateTicketsSheetProps {
	sop: SOP | null;
	/** Which pool to show; defaults to the SOP's mapped tickets. */
	source?: TicketSheetSource;
	onOpenChange: (open: boolean) => void;
}

export const CandidateTicketsSheet = ({ sop, source = 'mapped', onOpenChange }: CandidateTicketsSheetProps) => (
	<Sheet open={Boolean(sop)} onOpenChange={onOpenChange}>
		<SheetContent side='right' className='flex w-[70vw] flex-col gap-0 bg-background p-0 backdrop-blur-none sm:max-w-none'>
			{sop && <CandidateTicketsBody sop={sop} source={source} />}
		</SheetContent>
	</Sheet>
);
